import React from "react";

/**
 * Hero illustration inside the TripSetup modal header — soft moon glow,
 * cattail silhouettes and drifting fireflies. Purely decorative; wrapped
 * in `pointer-events: none` so it never intercepts form clicks.
 */
export const TripSetupHeroSVG = () => (
  <div
    className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-40 md:h-44 overflow-hidden rounded-t-2xl"
    aria-hidden="true"
    data-testid="tripsetup-hero-illustration"
  >
    <svg viewBox="0 0 640 200" preserveAspectRatio="xMidYMax slice" className="w-full h-full opacity-90">
      <defs>
        <radialGradient id="tsHeaderMoon" cx="80%" cy="18%" r="55%">
          <stop offset="0%" stopColor="#FFD166" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#F4A93A" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#0A1A26" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="tsHeaderFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A1A26" stopOpacity="0" />
          <stop offset="60%" stopColor="#0A1A26" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#0A1A26" stopOpacity="1" />
        </linearGradient>
        <radialGradient id="tsFireflyGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFF4C1" stopOpacity="1" />
          <stop offset="35%" stopColor="#FFD166" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#FFD166" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Moon glow behind headline */}
      <rect x="0" y="0" width="640" height="200" fill="url(#tsHeaderMoon)" />

      {/* Distant cattail silhouettes — right side */}
      <g opacity="0.32" fill="#1D3A4D">
        <path d="M540 200 L540 130 Q540 122 545 122 Q550 122 550 130 L550 200 Z" />
        <ellipse cx="545" cy="118" rx="6" ry="16" />
        <path d="M568 200 L568 145 Q568 138 573 138 Q578 138 578 145 L578 200 Z" />
        <ellipse cx="573" cy="132" rx="5" ry="14" />
        <path d="M598 200 L598 118 Q598 110 603 110 Q608 110 608 118 L608 200 Z" />
        <ellipse cx="603" cy="105" rx="7" ry="18" />
        <path d="M622 200 L622 150 Q622 143 627 143 Q632 143 632 150 L632 200 Z" />
        <ellipse cx="627" cy="138" rx="5" ry="14" />
      </g>

      {/* Foreground cattails — left */}
      <g opacity="0.5" fill="#0F2536">
        <path d="M12 200 L12 108 Q12 100 18 100 Q24 100 24 108 L24 200 Z" />
        <ellipse cx="18" cy="94" rx="8" ry="22" />
        <path d="M42 200 L42 132 Q42 124 47 124 Q52 124 52 132 L52 200 Z" />
        <ellipse cx="47" cy="118" rx="6" ry="16" />
        <path d="M70 200 L70 88 Q70 80 76 80 Q82 80 82 88 L82 200 Z" />
        <ellipse cx="76" cy="72" rx="9" ry="24" />
      </g>

      {/* Reeds fanning out along the base */}
      <g stroke="#0F2536" strokeWidth="1.4" strokeLinecap="round" opacity="0.55" fill="none">
        <path d="M96 200 Q100 172 108 158" />
        <path d="M118 200 Q124 178 134 168" />
        <path d="M148 200 Q150 176 156 162" />
        <path d="M178 200 Q182 180 188 168" />
        <path d="M482 200 Q478 178 470 166" />
        <path d="M508 200 Q504 172 496 158" />
        <path d="M528 200 Q526 178 520 168" />
      </g>

      {/* Fireflies — glowy orbs pulsing at different offsets */}
      <g>
        <circle cx="140" cy="70" r="12" fill="url(#tsFireflyGlow)">
          <animate attributeName="opacity" values="0.55;1;0.55" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="140" cy="70" r="2.4" fill="#FFF4C1">
          <animate attributeName="opacity" values="0.8;1;0.8" dur="3.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="230" cy="42" r="9" fill="url(#tsFireflyGlow)">
          <animate attributeName="opacity" values="0.4;0.95;0.4" dur="4.1s" repeatCount="indefinite" />
        </circle>
        <circle cx="230" cy="42" r="2" fill="#FFF4C1" />
        <circle cx="310" cy="88" r="14" fill="url(#tsFireflyGlow)">
          <animate attributeName="opacity" values="0.6;1;0.6" dur="2.7s" repeatCount="indefinite" />
        </circle>
        <circle cx="310" cy="88" r="2.6" fill="#FFF4C1" />
        <circle cx="400" cy="52" r="10" fill="url(#tsFireflyGlow)">
          <animate attributeName="opacity" values="0.45;0.9;0.45" dur="3.6s" repeatCount="indefinite" />
        </circle>
        <circle cx="400" cy="52" r="2.2" fill="#FFF4C1" />
        <circle cx="460" cy="96" r="11" fill="url(#tsFireflyGlow)">
          <animate attributeName="opacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="460" cy="96" r="2.3" fill="#FFF4C1" />
        <circle cx="80" cy="30" r="7" fill="url(#tsFireflyGlow)">
          <animate attributeName="opacity" values="0.3;0.85;0.3" dur="4.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="80" cy="30" r="1.6" fill="#FFF4C1" />
      </g>

      {/* Bottom fade so the content beneath reads clearly */}
      <rect x="0" y="0" width="640" height="200" fill="url(#tsHeaderFade)" />
    </svg>
  </div>
);

export default TripSetupHeroSVG;
